import { apiRequest, ApiClientError } from '@/lib/api/client'
import { landingApiRequest } from '@/lib/landing-api'

export type AiSplit = { name: string; user_id: string; percentage: number }
export type AiAdminSettings = {
  enabled: boolean
  model: string
  minimum_credit_price_cents: number
  instructions: string
  splits: AiSplit[]
  revision: number
  has_api_key: boolean
  history_days: number
}
export type AiCampaignSettings = {
  enabled: boolean
  name: string
  welcome_message: string | null
  instructions: string | null
  credit_price_cents: number
  revision: number
  minimum_credit_price_cents: number
  platform_ready: boolean
}
export type AiOffer = {
  credits: number
  amount_cents: number
  institution_amount_cents: number
  numbers_quantity: number
}
export type AiPurchase = AiOffer & {
  id: string
  payment_id: number | null
  numbers_assigned: number
  status: 'creating' | 'creation_uncertain' | 'pending' | 'expired' | 'paid'
  pix_key: string | null
  created_at: string
  expires_at: string
  credited_at: string | null
}
export type AiPurchases = {
  items: AiPurchase[]
  page: number
  last_page: number
  paid_count: number
  total_paid_cents: number
}
export type AiConversation = { id: string; title: string; updated_at: string }
export type AiTurn = {
  id: number
  request_id: string
  message: string
  answer: string | null
  status: 'pending' | 'completed' | 'failed'
  created_at: string
}
export type AiHistory = { id: string; title: string; turns: AiTurn[] }
export type AiState = {
  name: string
  welcome_message: string | null
  enabled: boolean
  institution_name: string
  history_days: number
  balance: number
  spent: number
  credit_price_cents: number
  conversations: AiConversation[]
  offers: AiOffer[]
  sales_message: string | null
  purchases: AiPurchases
}
const path = (id: number) => `/api/me/campaigns/${id}/ai`
export const aiMoney = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
export const aiError = (error: unknown) =>
  error instanceof ApiClientError
    ? Object.values(error.errors ?? {}).flat()[0] || error.message
    : 'Não foi possível concluir. Confira sua conexão e tente novamente.'
export const getAiAdmin = (signal?: AbortSignal) =>
  apiRequest<AiAdminSettings>('/api/admin/ai', { signal, cache: 'no-store' })
export const saveAiAdmin = (s: AiAdminSettings, apiKey: string) =>
  apiRequest<AiAdminSettings>('/api/admin/ai', {
    method: 'PUT',
    body: JSON.stringify({
      enabled: s.enabled,
      model: s.model,
      minimum_credit_price_cents: s.minimum_credit_price_cents,
      instructions: s.instructions,
      splits: s.splits,
      revision: s.revision,
      ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
    }),
  })
export const getAiCampaign = (id: number, signal?: AbortSignal) =>
  apiRequest<AiCampaignSettings>(`/api/campaigns/${id}/ai`, { signal, cache: 'no-store' })
export const saveAiCampaign = (id: number, s: AiCampaignSettings) =>
  apiRequest<AiCampaignSettings>(`/api/campaigns/${id}/ai`, {
    method: 'PUT',
    body: JSON.stringify({
      enabled: s.enabled,
      name: s.name,
      welcome_message: s.welcome_message,
      instructions: s.instructions,
      credit_price_cents: s.credit_price_cents,
      revision: s.revision,
    }),
  })
export const getAiState = (id: number, signal?: AbortSignal) =>
  landingApiRequest<AiState>(path(id), { signal })
export const getAiAvailability = (id: number, signal?: AbortSignal) =>
  landingApiRequest<{ enabled: boolean }>(`${path(id)}/availability`, { signal })
export const getAiHistory = (id: number, conversation: string, signal?: AbortSignal) =>
  landingApiRequest<AiHistory>(`${path(id)}/conversations/${conversation}`, { signal })
export const sendAiMessage = (
  id: number,
  data: { message: string; request_id: string; conversation_id: string | null }
) =>
  landingApiRequest<{ conversation_id: string; turn: AiTurn }>(`${path(id)}/messages`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
export const buyAiCredits = (id: number, offer: AiOffer, requestId: string) =>
  landingApiRequest<AiPurchase>(`${path(id)}/purchases`, {
    method: 'POST',
    body: JSON.stringify({
      credits: offer.credits,
      request_id: requestId,
      expected_amount_cents: offer.amount_cents,
      expected_institution_amount_cents: offer.institution_amount_cents,
      expected_numbers_quantity: offer.numbers_quantity,
    }),
  })
export const getAiQuote = (id: number, credits: number, signal?: AbortSignal) =>
  landingApiRequest<AiOffer>(`${path(id)}/quote`, {
    method: 'POST',
    signal,
    body: JSON.stringify({ credits }),
  })
export const getAiPurchase = (id: number, purchaseId: string) =>
  landingApiRequest<AiPurchase>(`${path(id)}/purchases/${purchaseId}`)
export const getAiPurchases = (id: number, page: number) =>
  landingApiRequest<AiPurchases>(`${path(id)}/purchases?page=${page}`)
